import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import { config } from "../config";
import { getConsumerClerkToken, getOperation } from "../services/api";
import type { IOperationResponse } from "../services/api";
import {
  openWidgetSse,
  type WidgetSseConnection,
} from "../utils/widgetSse";
import { getEvents, safeCapture } from "../analytics";

/** Statuses past which an operation no longer changes (SSE can stop). */
const TERMINAL_STATUSES = new Set([
  "completed",
  "failed",
  "cancelled",
  "canceled",
  "expired",
]);

/** REST safety-net while SSE is open but deltas are dropped/buffered. */
const DEFAULT_RECONCILE_INTERVAL_MS = 8_000;

export function isOperationNonTerminal(op: IOperationResponse): boolean {
  return !TERMINAL_STATUSES.has((op.status ?? "").toLowerCase());
}

export function hasNonTerminalOperations(ops: IOperationResponse[]): boolean {
  return ops.some(isOperationNonTerminal);
}

/** Apply an operation delta to a list (replace by id, otherwise prepend). */
export function upsertOperationInList(
  list: IOperationResponse[],
  op: IOperationResponse
): IOperationResponse[] {
  const idx = list.findIndex((o) => o.operationId === op.operationId);
  if (idx >= 0) {
    const next = [...list];
    next[idx] = op;
    return next;
  }
  return [op, ...list];
}

function closeSse(
  conn: WidgetSseConnection | null,
  connRef: MutableRefObject<WidgetSseConnection | null>
): void {
  if (!conn) return;
  conn.close();
  if (connRef.current === conn) {
    connRef.current = null;
  }
}

/** Nest / proxies sometimes double-encode the SSE payload. */
function parseSsePayload(raw: unknown): Record<string, unknown> | null {
  try {
    let data: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (typeof data === "string") {
      data = JSON.parse(data);
    }
    if (!data || typeof data !== "object") return null;
    return data as Record<string, unknown>;
  } catch {
    return null;
  }
}

export interface UseOperationSSEOptions {
  /** If set, only stream while this operation exists in the list and is non-terminal. */
  watchOperationId?: string;
  /**
   * Poll REST while non-terminal ops are loaded so a “zombie” SSE connection
   * (heartbeats only, missed deltas) cannot leave the UI stuck. Set `0` to disable.
   */
  reconcileIntervalMs?: number;
}

/**
 * Keeps operation statuses live via Server-Sent Events.
 *
 * Mirrors the raas-widgets-flow-m2m approach but is decoupled from any global
 * store: the caller owns the operations list and provides an `onUpsert` callback
 * to apply each delta. The stream is only kept open while at least one loaded
 * operation is non-terminal (or, in watch mode, while the watched operation is
 * non-terminal).
 *
 * Uses fetch (not EventSource) so ngrok free tunnels receive the skip-warning
 * header and the stream can connect.
 *
 * Also runs a REST reconcile interval while active — claim/deferred completions
 * can land on origination SSE for the sender while the recipient's operation
 * stream drops or buffers deltas; without polling the list stays on `processing`
 * until a full reload.
 */
export function useOperationSSE(
  linkId: string | null | undefined,
  operations: IOperationResponse[],
  onUpsert: (op: IOperationResponse) => void,
  onReconnect?: () => void,
  options?: UseOperationSSEOptions
): void {
  const watchOperationId = options?.watchOperationId;
  const reconcileIntervalMs =
    options?.reconcileIntervalMs ?? DEFAULT_RECONCILE_INTERVAL_MS;
  const connRef = useRef<WidgetSseConnection | null>(null);
  const isFirstOpenRef = useRef(true);

  const operationsRef = useRef(operations);
  const onUpsertRef = useRef(onUpsert);
  const onReconnectRef = useRef(onReconnect);
  operationsRef.current = operations;
  onUpsertRef.current = onUpsert;
  onReconnectRef.current = onReconnect;

  const hasActiveOps = useMemo(() => {
    if (!watchOperationId) {
      return hasNonTerminalOperations(operations);
    }
    const op = operations.find((o) => o.operationId === watchOperationId);
    // Open as soon as the id is known; close once we know it is terminal.
    return !op || isOperationNonTerminal(op);
  }, [operations, watchOperationId]);

  const reconcile = () => {
    if (watchOperationId && linkId) {
      void getOperation(linkId, watchOperationId, {
        raasSilentOffline: true,
      })
        .then((fetched) => onUpsertRef.current(fetched))
        .catch((err) =>
          console.error(
            "[useOperationSSE] reconcile: failed to refresh watched operation",
            err
          )
        );
      return;
    }
    onReconnectRef.current?.();
  };

  // Safety net: keep REST in sync even if SSE stays open without deltas.
  useEffect(() => {
    if (!linkId || !hasActiveOps || reconcileIntervalMs <= 0) return;

    const id = window.setInterval(reconcile, reconcileIntervalMs);
    return () => window.clearInterval(id);
  }, [linkId, hasActiveOps, watchOperationId, reconcileIntervalMs]);

  useEffect(() => {
    if (!linkId || !hasActiveOps) {
      closeSse(connRef.current, connRef);
      return;
    }

    if (connRef.current) return;

    isFirstOpenRef.current = true;
    const url = `${config.API_BASE_URL}/widget/links/${linkId}/operations/sse`;

    const maybeClose = (): void => {
      const ops = operationsRef.current;
      if (watchOperationId) {
        const op = ops.find((o) => o.operationId === watchOperationId);
        if (op && !isOperationNonTerminal(op)) {
          closeSse(connRef.current, connRef);
        }
        return;
      }
      if (!hasNonTerminalOperations(ops)) {
        closeSse(connRef.current, connRef);
      }
    };

    const conn = openWidgetSse({
      url,
      headers: { "x-link-id": linkId },
      getHeaders: async () => {
        const token = await getConsumerClerkToken();
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        return headers;
      },
      onOpen: () => {
        if (!isFirstOpenRef.current) {
          reconcile();
        }
        isFirstOpenRef.current = false;
      },
      onMessage: (event) => {
        try {
          const data = parseSsePayload(event.data);
          if (!data) return;

          if (data.type === "heartbeat") {
            maybeClose();
            return;
          }

          const updated = data as unknown as IOperationResponse;
          if (!updated?.operationId) return;

          if (watchOperationId && updated.operationId !== watchOperationId) {
            maybeClose();
            return;
          }

          safeCapture(getEvents().OPERATION_STATUS_CHANGED, {
            operation_id: updated.operationId,
            status: updated.status,
            funding_status: updated.fundingStatus,
            quote_id: updated.quoteId,
            send_amount: updated.sendAmount,
            receive_amount: updated.receiveAmount,
          });

          onUpsertRef.current(updated);
          maybeClose();
        } catch (e) {
          console.error("[useOperationSSE] message parse error", e);
        }
      },
      onError: () => {
        console.warn("[useOperationSSE] connection issue (auto-retry)", linkId);
      },
    });
    connRef.current = conn;

    return () => {
      closeSse(conn, connRef);
    };
  }, [linkId, hasActiveOps, watchOperationId]);
}
